import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Owner-managed (CMS) homepage image overrides.
 *
 * The stock fallback must never render, and so never be downloaded, before the
 * CMS data is known. `pick()` therefore returns `{ src: null, loading: true }`
 * until the request finishes. After that it returns the CMS photo when one
 * exists, otherwise the stock fallback. A failed or timed-out request counts as
 * "no override" so the page never stays blank.
 *
 * The last result is cached for the browser session (shared by Home and
 * Commercial) so moving between pages shows the right photo instantly. It is
 * still revalidated in the background on each mount.
 */

const REQUEST_TIMEOUT_MS = 8000;

let cache = null; // { [slot]: { image_data, alt_text } } once loaded
let inflight = null; // shared promise so simultaneous mounts make one request

function fetchOverrides() {
  if (!inflight) {
    inflight = api
      .get("/public/homepage-images", { timeout: REQUEST_TIMEOUT_MS })
      .then((r) => {
        cache = r.data && typeof r.data === "object" ? r.data : {};
        return cache;
      })
      .catch(() => {
        // Keep a previous good result. With nothing cached, fall back to stock.
        if (!cache) cache = {};
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Drop the cached overrides (call after an admin saves or clears a photo). */
export function invalidateHomepageImages() {
  cache = null;
}

export default function useHomepageImages() {
  const [bySlot, setBySlot] = useState(cache);

  useEffect(() => {
    let cancelled = false;
    fetchOverrides().then((data) => {
      if (!cancelled) setBySlot(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const loading = bySlot === null;

  const pick = (slot, fallbackSrc, fallbackAlt = "") => {
    if (loading) return { src: null, alt: fallbackAlt, loading: true };
    const item = bySlot[slot];
    if (item && item.image_data) {
      return { src: item.image_data, alt: item.alt_text || fallbackAlt, loading: false, fromCms: true };
    }
    return { src: fallbackSrc, alt: fallbackAlt, loading: false, fromCms: false };
  };

  return { pick, bySlot, loading };
}
